/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Papa from 'papaparse';
import type {
    PlotlyMetersMap,
    PlotlyReferenceKey,
} from '@/types/plotlyConfig';
import type { SensorCsvRow } from '@/types/sensorCsv';

/* Import libraries */
import { getSensorCsvSelection } from '@/lib/sensors/csv/sensorCsv';

function getReferenceKey(anomaly: string): PlotlyReferenceKey | null {
    if (anomaly === 'bl_night' || anomaly === 'bl') {
        return 'base_load';
    }

    if (anomaly === 'heating_energy') {
        return 'heating_energy';
    }

    if (anomaly === 'pv') {
        return 'pv_plots';
    }

    return null;
}

function getRanges(referenceKey: PlotlyReferenceKey): string[] {
    if (referenceKey === 'pv_plots') {
        return [];
    }

    return referenceKey === 'base_load' ? ['14d', 'all'] : ['all'];
}

function isValidSubsensor(value: string): boolean {
    return /^[A-Za-z]{1,2}_[0-9]{8}$/.test(value);
}

export function csvToPlotlyMeters(csv: string): PlotlyMetersMap {
    const parsedCsv = Papa.parse<SensorCsvRow>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
    });

    return parsedCsv.data.reduce<PlotlyMetersMap>((accumulator, row) => {
        if (row.Enabled !== '1' || !row.Sensor || !row.Anomaly) {
            return accumulator;
        }

        const sensorSelection = getSensorCsvSelection(row.Sensor);

        if (!sensorSelection) {
            return accumulator;
        }

        const { deviceName: meterKey, sensorType: subsensor } = sensorSelection;
        const referenceKey = getReferenceKey(row.Anomaly);

        if (!referenceKey) {
            return accumulator;
        }

        if (!accumulator[meterKey]) {
            accumulator[meterKey] = {
                reference: {},
            };
        }

        if (!accumulator[meterKey].reference[referenceKey]) {
            accumulator[meterKey].reference[referenceKey] = {
                ranges: getRanges(referenceKey),
                subsensors: [],
            };
        }

        const subsensors = accumulator[meterKey].reference[referenceKey].subsensors;

        if (isValidSubsensor(subsensor) && !subsensors.includes(subsensor)) {
            subsensors.push(subsensor);
        }

        return accumulator;
    }, {});
}
